'use client';

import { GseImportCard, type GseImportFileResult, GseXlsxExportCard } from '@slims/ui/molecules';
import { useState } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/utils/api';
import { useContextOrg } from '@/hooks/use-context-org';

/**
 * Wiring dei report GSE xlsx: download dello zip simulato «Dett_Misura»
 * (/api/reports/gse-xlsx) e import massivo dei file ufficiali del portale GSE
 * (/api/energy/gse-import). Le card in @slims/ui restano pure presentazione.
 */
export function GseXlsxCards() {
	return (
		<>
			<GseXlsxExport />
			<GseImport />
		</>
	);
}

function GseXlsxExport() {
	const [busy, setBusy] = useState(false);
	const contextOrg = useContextOrg();

	async function handleExport(from: string, to: string) {
		if (!contextOrg) {
			toast.error('Nessuna configurazione selezionata');
			return;
		}
		setBusy(true);
		try {
			const res = await apiFetch(
				`/api/reports/gse-xlsx?from=${from}&to=${to}&orgId=${contextOrg.orgId}`,
			);
			if (!res.ok) {
				const body = (await res.json().catch(() => null)) as { error?: string } | null;
				throw new Error(body?.error ?? `Errore ${res.status}`);
			}
			const blob = await res.blob();
			const cd = res.headers.get('Content-Disposition') ?? '';
			const filename = /filename="([^"]+)"/.exec(cd)?.[1] ?? `GSE_DettMisura_${from}_${to}.zip`;
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			a.click();
			URL.revokeObjectURL(url);
			toast.success(`ZIP generato: ${filename}`);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore nella generazione dello ZIP');
		} finally {
			setBusy(false);
		}
	}

	return <GseXlsxExportCard busy={busy} onExport={handleExport} />;
}

function GseImport() {
	const [busy, setBusy] = useState(false);
	const [results, setResults] = useState<GseImportFileResult[] | null>(null);
	const [totalWritten, setTotalWritten] = useState<number | null>(null);
	const contextOrg = useContextOrg();

	async function handleImport(files: File[]) {
		if (!contextOrg) {
			toast.error('Nessuna configurazione selezionata');
			return;
		}
		setBusy(true);
		try {
			const form = new FormData();
			for (const f of files) form.append('files', f);
			const res = await apiFetch(`/api/energy/gse-import?orgId=${contextOrg.orgId}`, {
				method: 'POST',
				body: form,
			});
			const body = (await res.json().catch(() => null)) as
				| { files?: GseImportFileResult[]; totalWritten?: number; error?: string }
				| null;
			if (!res.ok) throw new Error(body?.error ?? `Errore ${res.status}`);
			setResults(body?.files ?? []);
			setTotalWritten(body?.totalWritten ?? 0);
			const failed = (body?.files ?? []).filter((f) => f.error || f.skipped).length;
			if (failed > 0) toast.warning(`Import completato con ${failed} file saltati o in errore`);
			else toast.success(`Import completato: ${body?.totalWritten ?? 0} letture orarie`);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Errore durante l'import");
		} finally {
			setBusy(false);
		}
	}

	return (
		<GseImportCard busy={busy} results={results} totalWritten={totalWritten} onImport={handleImport} />
	);
}
